// 列表、侧栏和导出共用已保存的 screeningStatus；旧记录才兼容 qualified，不从客户审核状态推断。
export const AUDIT_OPTIONS = [
  { value: '0', label: '待审核', tone: 'warning' },
  { value: '1', label: '已通过', tone: 'success' },
  { value: '2', label: '已驳回', tone: 'danger' },
  { value: '3', label: '已支付', tone: 'info' },
  { value: '4', label: '已退款', tone: 'neutral' },
];
export const QUALIFIED_OPTIONS = [
  { value: 'PASSED', label: '已通过', tone: 'success' },
  { value: 'REJECTED', label: '未通过', tone: 'danger' },
  { value: 'PENDING_CONFIRMATION', label: '待确认', tone: 'warning' },
];
export const EXAM_OPTIONS = [
  { value: '0', label: '未预约', tone: 'neutral' },
  { value: '1', label: '已预约', tone: 'info' },
  { value: '2', label: '已取消', tone: 'warning' },
  { value: '3', label: '已到检', tone: 'success' },
  { value: '4', label: '已出报告', tone: 'success' },
  { value: '5', label: '已传报告', tone: 'success' },
];
export const ASSESSMENT_OPTIONS = [
  { value: 'PENDING_REVIEW', label: '待评估', tone: 'neutral' },
  { value: 'REPORT_REJECTED', label: '不合格', tone: 'danger' },
  { value: 'REPORT_APPROVED', label: '合格', tone: 'success' },
  { value: 'FOLLOW_UP', label: '待复核跟进', tone: 'warning' },
];
export const DISEASE_OPTIONS = [
  { value: 'hypertension', label: '高血压' },
  { value: 'diabetes', label: '糖尿病' },
  { value: 'uric', label: '高尿酸' },
];
const FLAGS = {
  AGE_REVIEW: '年龄复核', RECENT_CARDIO_EVENT: '近期心血管事件', RENAL_FUNCTION: '肾功能',
  CURRENT_HOSPITALIZATION: '近期住院', ACUTE_RESPIRATORY: '急性呼吸系统疾病', DIABETES_DIAGNOSIS: '糖尿病确诊',
};
export function hasValue(value) { return value !== null && value !== undefined && value !== ''; }
export function statusInfo(options, value, empty = '暂无数据') {
  if (!hasValue(value)) return { label: empty, tone: 'neutral' };
  return options.find(item => item.value === String(value)) || { label: `未知状态（${value}）`, tone: 'neutral' };
}
export function diseaseText(value) {
  return (DISEASE_OPTIONS.find(item => item.value === value) || {}).label || value || '暂无病种';
}
export function qualifiedText(value) {
  return qualifiedInfo(value).label;
}
export function screeningStatus(value) {
  const row = value && typeof value === 'object' ? value : { qualified: value };
  if (hasValue(row.screeningStatus)) return String(row.screeningStatus);
  if (String(row.qualified) === '1') return 'PASSED';
  if (String(row.qualified) === '0') return 'REJECTED';
  return '';
}
export function isScreeningPassed(value) { return screeningStatus(value) === 'PASSED'; }
export function isScreeningUnsuitable(value) { return ['REJECTED', 'PENDING_CONFIRMATION'].includes(screeningStatus(value)); }
export const SCREENING_MEDICAL_CONFIRMATION = '仅问卷初筛已通过的客户可以审核通过；未通过、待确认或结果缺失的客户不可通过，请联系我司进行进一步医学确认。';
export function qualifiedInfo(value) {
  const status = screeningStatus(value);
  return QUALIFIED_OPTIONS.find(item => item.value === status) || { label: status ? '初筛状态异常' : '暂无初筛结果', tone: 'neutral' };
}
export function contraindicationItems(value) {
  const items = parseContent(value);
  return Array.isArray(items) ? items.filter(item => item && typeof item === 'object').map(item => ({
    reason: typeof item.reason === 'string' && item.reason.trim() ? item.reason : '未提供具体原因，请联系我司核实',
    answerSummary: typeof item.answerSummary === 'string' ? item.answerSummary.trim() : '',
  })) : [];
}
export function contraindicationsText(value) {
  return contraindicationItems(value).map(item => item.reason + (item.answerSummary ? `（${item.answerSummary}）` : '')).join('；');
}
export function questionnaireText(row) {
  if (!hasValue(row.submitId)) return '暂无问卷数据';
  return `已提交${hasValue(row.revisionNo) ? ` · 第 ${row.revisionNo} 次填写` : ''}`;
}
export function examInfo(row) {
  if (hasValue(row.examStatus)) return statusInfo(EXAM_OPTIONS, row.examStatus);
  return { label: row.examRecordId === null ? '无体检记录' : '暂无体检状态', tone: 'neutral' };
}
export function maskIdCard(value) {
  if (!value) return '—';
  const text = String(value);
  if (text.includes('*')) return text;
  if (text.length === 18) return `${text.slice(0, 6)}********${text.slice(-4)}`;
  if (text.length === 15) return `${text.slice(0, 4)}********${text.slice(-3)}`;
  return '****';
}
export function formatTime(value) {
  if (!hasValue(value)) return '—';
  // 服务端可能返回带时区的 ISO 日期、时间戳或本地日期字符串。
  const source = typeof value === 'string' && /^\d{4}-\d{2}-\d{2} /.test(value) ? value.replace(' ', 'T') : value;
  const date = new Date(source);
  if (isNaN(date.getTime())) return String(value);
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
export function reservationText(row) {
  if (!row.reservationDate) return '—';
  const date = String(row.reservationDate).replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3');
  return `${date}${row.reservationTime ? ` ${row.reservationTime}` : ''}`;
}
export function parseContent(value) {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch (e) { return value; }
}
const CONTENT_LABELS = {
  conclusion: '结论', summary: '摘要', riskLevel: '风险分层', risk_level: '风险分层',
  evidence: '证据', missingItems: '缺失项', missing_items: '缺失项', recommendations: '建议',
  reason: '原因', reasons: '原因', result: '结果', status: '状态', interpretation: '解读',
};
// 仅呈现接口原文，不在前端生成诊断、评级或通过结论，未知字段保留原始名称。
export function contentText(value) {
  const data = parseContent(value);
  if (data === null || data === undefined || data === '') return '';
  if (Array.isArray(data)) return data.map(item => contentText(item)).join('\n');
  if (typeof data === 'object') return Object.keys(data).map(key => `${CONTENT_LABELS[key] || key}：${contentText(data[key])}`).join('\n');
  return String(data);
}
export function flagsText(value) {
  const flags = parseContent(value);
  if (Array.isArray(flags)) return flags.map(flag => FLAGS[flag] || contentText(flag)).join('、') || '无';
  return contentText(flags) || '暂无风险提示';
}
// 带 BOM 的标准 CSV：防止公式注入；长数字、手机号强制按文本保留。
function csvCell(value) {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[\s\uFEFF]*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text) || /^\d{11,}$/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}
export function customerCsv(rows) {
  const columns = [
    ['客户ID', r => r.customerId], ['客户姓名', r => r.personName], ['手机号', r => r.phone],
    ['身份证（脱敏）', r => maskIdCard(r.idCard)], ['业务员', r => r.salesmanName],
    ['客户状态', r => statusInfo(AUDIT_OPTIONS, r.auditStatus).label], ['病种', r => diseaseText(r.disease)],
    ['问卷状态', questionnaireText], ['问卷名称', r => r.questionnaireName], ['问卷模板版本', r => r.questionnaireVersion],
    ['问卷提交ID', r => r.submitId], ['初筛结果', r => qualifiedText(r)],
    ['绝对禁忌原因及填写摘要', r => contraindicationsText(r.absoluteContraindications)],
    ['相对禁忌原因及填写摘要', r => contraindicationsText(r.relativeContraindications)],
    ['提交时间', r => formatTime(r.submitTime)],
    ['体检状态', r => examInfo(r).label], ['体检套餐', r => r.packageName], ['预约城市', r => r.cityName],
    ['体检机构', r => r.unitsName], ['预约日期及时段', reservationText], ['实际到检日期', r => r.checkupDate],
    ['体检记录更新时间', r => formatTime(r.examUpdatedAt)],
    ['报告评审', r => statusInfo(ASSESSMENT_OPTIONS, r.assessmentStatus).label],
    ['报告评审解读', r => contentText(r.assessmentInterpretation)], ['报告评审时间', r => formatTime(r.reviewedAt)],
  ];
  return '\uFEFF' + [columns.map(c => csvCell(c[0])).join(','), ...rows.map(row => columns.map(c => csvCell(c[1](row))).join(','))].join('\r\n');
}
export function downloadCustomerCsv(rows) {
  const url = URL.createObjectURL(new Blob([customerCsv(rows)], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `三高客户_${formatTime(new Date()).replace(/[- :]/g, '')}_${rows.length}条.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
